import {
  CHAIN_DEFINITIONS,
  type ChainSpec,
} from '../schema/mapping';

type DiagramKey = 'body' | 'left-hand' | 'right-hand';

type HandFingerTemplate = {
  fingerName: 'Thumb' | 'Index' | 'Middle' | 'Ring' | 'Pinky';
  segments: readonly string[];
  metacarpalRole: string | null;
};

type RoleGraphConnection = {
  from: string;
  to: string;
  diagramKey: DiagramKey | null;
};

export type RoleChainRelationshipKind =
  | 'singleBone'
  | 'start'
  | 'end'
  | 'intermediate';

export interface RoleChainPath {
  chainName: string;
  startRole: string;
  endRole: string;
  rolePath: readonly string[];
}

export interface RoleChainDiagramPathSegment {
  from: string;
  to: string;
  diagramKey: string;
}

export interface RoleChainDiagramPath extends RoleChainPath {
  segments: readonly RoleChainDiagramPathSegment[];
  nodeDiagramKey: string | null;
}

export interface RoleChainRelationship extends RoleChainPath {
  kind: RoleChainRelationshipKind;
  isSingleBoneChain: boolean;
}

const BODY_DIAGRAM_KEY: DiagramKey = 'body';
const LEFT_HAND_DIAGRAM_KEY: DiagramKey = 'left-hand';
const RIGHT_HAND_DIAGRAM_KEY: DiagramKey = 'right-hand';

const EMPTY_RELATIONSHIPS: readonly RoleChainRelationship[] = [];

const BODY_ROLE_NAMES = [
  'Head',
  'Neck',
  'Chest',
  'Spine',
  'Pelvis',
  'Root',
  'LeftClavicle',
  'LeftUpperArm',
  'LeftLowerArm',
  'LeftHand',
  'RightClavicle',
  'RightUpperArm',
  'RightLowerArm',
  'RightHand',
  'LeftUpperLeg',
  'LeftLowerLeg',
  'LeftHock',
  'LeftFoot',
  'LeftToe',
  'RightUpperLeg',
  'RightLowerLeg',
  'RightHock',
  'RightFoot',
  'RightToe',
] as const;

const BODY_ROLE_CONNECTIONS: readonly RoleGraphConnection[] = [
  { from: 'Head', to: 'Neck', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Neck', to: 'Chest', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Chest', to: 'Spine', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Spine', to: 'Pelvis', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Pelvis', to: 'Root', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Chest', to: 'LeftClavicle', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'LeftClavicle', to: 'LeftUpperArm', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'LeftUpperArm', to: 'LeftLowerArm', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'LeftLowerArm', to: 'LeftHand', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Chest', to: 'RightClavicle', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'RightClavicle', to: 'RightUpperArm', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'RightUpperArm', to: 'RightLowerArm', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'RightLowerArm', to: 'RightHand', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Pelvis', to: 'LeftUpperLeg', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'LeftUpperLeg', to: 'LeftLowerLeg', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'LeftLowerLeg', to: 'LeftHock', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'LeftHock', to: 'LeftFoot', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'LeftFoot', to: 'LeftToe', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'Pelvis', to: 'RightUpperLeg', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'RightUpperLeg', to: 'RightLowerLeg', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'RightLowerLeg', to: 'RightHock', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'RightHock', to: 'RightFoot', diagramKey: BODY_DIAGRAM_KEY },
  { from: 'RightFoot', to: 'RightToe', diagramKey: BODY_DIAGRAM_KEY },
] as const;

const EXTRA_ARM_SUFFIXES = ['_2', '_3', '_4', '_5'] as const;

const HAND_FINGER_TEMPLATES: readonly HandFingerTemplate[] = [
  {
    fingerName: 'Thumb',
    segments: ['1', '2', '3'],
    metacarpalRole: null,
  },
  {
    fingerName: 'Index',
    segments: ['1', '2', '3'],
    metacarpalRole: 'IndexMetacarpal',
  },
  {
    fingerName: 'Middle',
    segments: ['1', '2', '3'],
    metacarpalRole: 'MiddleMetacarpal',
  },
  {
    fingerName: 'Ring',
    segments: ['1', '2', '3'],
    metacarpalRole: 'RingMetacarpal',
  },
  {
    fingerName: 'Pinky',
    segments: ['1', '2', '3'],
    metacarpalRole: 'PinkyMetacarpal',
  },
] as const;

function buildConnectionKey(from: string, to: string): string {
  return from < to ? `${from}::${to}` : `${to}::${from}`;
}

function addNodeDiagramKeys(
  roleNames: readonly string[],
  diagramKey: DiagramKey,
  nodeDiagramKeys: Map<string, string>,
) {
  for (const roleName of roleNames) {
    nodeDiagramKeys.set(roleName, diagramKey);
  }
}

function buildRoleAdjacency(
  connections: readonly Pick<RoleGraphConnection, 'from' | 'to'>[],
): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>();

  for (const { from, to } of connections) {
    if (!adjacency.has(from)) adjacency.set(from, new Set<string>());
    if (!adjacency.has(to)) adjacency.set(to, new Set<string>());
    adjacency.get(from)?.add(to);
    adjacency.get(to)?.add(from);
  }

  return adjacency;
}

export function findRolePath(
  adjacency: Map<string, Set<string>>,
  startRole: string,
  endRole: string,
): string[] | null {
  if (startRole === endRole) return [startRole];

  const queue = [startRole];
  const parentByRole = new Map<string, string | null>([[startRole, null]]);

  while (queue.length > 0) {
    const currentRole = queue.shift();
    if (!currentRole) continue;

    for (const nextRole of adjacency.get(currentRole) ?? []) {
      if (parentByRole.has(nextRole)) continue;
      parentByRole.set(nextRole, currentRole);

      if (nextRole === endRole) {
        const rolePath = [endRole];
        let roleCursor: string | null = currentRole;

        while (roleCursor) {
          rolePath.unshift(roleCursor);
          roleCursor = parentByRole.get(roleCursor) ?? null;
        }

        return rolePath;
      }

      queue.push(nextRole);
    }
  }

  return null;
}

function buildPrimaryHandRoleNames(side: 'Left' | 'Right'): string[] {
  const roleNames = [`${side}Hand`];

  for (const finger of HAND_FINGER_TEMPLATES) {
    if (finger.metacarpalRole) {
      roleNames.push(`${side}${finger.metacarpalRole}`);
    }

    for (const segment of finger.segments) {
      roleNames.push(`${side}${finger.fingerName}${segment}`);
    }
  }

  return roleNames;
}

function buildPrimaryHandConnections(
  side: 'Left' | 'Right',
  diagramKey: DiagramKey,
): RoleGraphConnection[] {
  const handRole = `${side}Hand`;
  const connections: RoleGraphConnection[] = [];

  for (const finger of HAND_FINGER_TEMPLATES) {
    let previousRole = handRole;

    if (finger.metacarpalRole) {
      const metacarpalRole = `${side}${finger.metacarpalRole}`;
      connections.push({
        from: previousRole,
        to: metacarpalRole,
        diagramKey,
      });
      previousRole = metacarpalRole;
    }

    for (const segment of finger.segments) {
      const segmentRole = `${side}${finger.fingerName}${segment}`;
      connections.push({
        from: previousRole,
        to: segmentRole,
        diagramKey,
      });
      previousRole = segmentRole;
    }
  }

  return connections;
}

function buildExtraArmConnections(): RoleGraphConnection[] {
  return EXTRA_ARM_SUFFIXES.flatMap((suffix) => [
    { from: 'Chest', to: `LeftClavicle${suffix}`, diagramKey: null },
    {
      from: `LeftClavicle${suffix}`,
      to: `LeftUpperArm${suffix}`,
      diagramKey: null,
    },
    {
      from: `LeftUpperArm${suffix}`,
      to: `LeftLowerArm${suffix}`,
      diagramKey: null,
    },
    {
      from: `LeftLowerArm${suffix}`,
      to: `LeftHand${suffix}`,
      diagramKey: null,
    },
    { from: 'Chest', to: `RightClavicle${suffix}`, diagramKey: null },
    {
      from: `RightClavicle${suffix}`,
      to: `RightUpperArm${suffix}`,
      diagramKey: null,
    },
    {
      from: `RightUpperArm${suffix}`,
      to: `RightLowerArm${suffix}`,
      diagramKey: null,
    },
    {
      from: `RightLowerArm${suffix}`,
      to: `RightHand${suffix}`,
      diagramKey: null,
    },
  ]);
}

const ROLE_NODE_DIAGRAM_KEY_BY_ROLE = new Map<string, string>();

addNodeDiagramKeys(BODY_ROLE_NAMES, BODY_DIAGRAM_KEY, ROLE_NODE_DIAGRAM_KEY_BY_ROLE);

const PRIMARY_LEFT_HAND_ROLE_NAMES = buildPrimaryHandRoleNames('Left');
const PRIMARY_RIGHT_HAND_ROLE_NAMES = buildPrimaryHandRoleNames('Right');

addNodeDiagramKeys(
  PRIMARY_LEFT_HAND_ROLE_NAMES,
  LEFT_HAND_DIAGRAM_KEY,
  ROLE_NODE_DIAGRAM_KEY_BY_ROLE,
);
addNodeDiagramKeys(
  PRIMARY_RIGHT_HAND_ROLE_NAMES,
  RIGHT_HAND_DIAGRAM_KEY,
  ROLE_NODE_DIAGRAM_KEY_BY_ROLE,
);

const ROLE_GRAPH_CONNECTIONS: readonly RoleGraphConnection[] = [
  ...BODY_ROLE_CONNECTIONS,
  ...buildPrimaryHandConnections('Left', LEFT_HAND_DIAGRAM_KEY),
  ...buildPrimaryHandConnections('Right', RIGHT_HAND_DIAGRAM_KEY),
  ...buildExtraArmConnections(),
];

const ROLE_ADJACENCY = buildRoleAdjacency(ROLE_GRAPH_CONNECTIONS);

const DIAGRAM_KEY_BY_CONNECTION = new Map<string, string>(
  ROLE_GRAPH_CONNECTIONS.flatMap((connection) =>
    connection.diagramKey === null
      ? []
      : [[buildConnectionKey(connection.from, connection.to), connection.diagramKey]],
  ),
);

export function isSingleBoneChain(chainSpec: ChainSpec): boolean {
  return (
    chainSpec.startRole === chainSpec.endRole &&
    chainSpec.minBones === 1 &&
    chainSpec.maxBones === 1
  );
}

function buildChainRolePathIndex(): Map<string, RoleChainPath> {
  const pathsByChainName = new Map<string, RoleChainPath>();

  for (const chainSpec of Object.values(CHAIN_DEFINITIONS)) {
    if (chainSpec.startRole === chainSpec.endRole) {
      pathsByChainName.set(chainSpec.name, {
        chainName: chainSpec.name,
        startRole: chainSpec.startRole,
        endRole: chainSpec.endRole,
        rolePath: [chainSpec.startRole],
      });
      continue;
    }

    const rolePath = findRolePath(
      ROLE_ADJACENCY,
      chainSpec.startRole,
      chainSpec.endRole,
    );
    if (!rolePath) continue;

    pathsByChainName.set(chainSpec.name, {
      chainName: chainSpec.name,
      startRole: chainSpec.startRole,
      endRole: chainSpec.endRole,
      rolePath,
    });
  }

  return pathsByChainName;
}

const CHAIN_ROLE_PATHS_BY_NAME = buildChainRolePathIndex();

function addRelationship(
  relationshipsByRole: Map<string, RoleChainRelationship[]>,
  roleName: string,
  relationship: RoleChainRelationship,
) {
  const existing = relationshipsByRole.get(roleName);
  if (existing) {
    existing.push(relationship);
    return;
  }

  relationshipsByRole.set(roleName, [relationship]);
}

function buildRoleRelationshipIndex(): Map<string, RoleChainRelationship[]> {
  const relationshipsByRole = new Map<string, RoleChainRelationship[]>();

  for (const chainSpec of Object.values(CHAIN_DEFINITIONS)) {
    const rolePath =
      CHAIN_ROLE_PATHS_BY_NAME.get(chainSpec.name)?.rolePath ?? [chainSpec.startRole];
    const baseRelationship = {
      chainName: chainSpec.name,
      startRole: chainSpec.startRole,
      endRole: chainSpec.endRole,
      rolePath,
      isSingleBoneChain: isSingleBoneChain(chainSpec),
    } satisfies Omit<RoleChainRelationship, 'kind'>;

    if (baseRelationship.isSingleBoneChain) {
      addRelationship(relationshipsByRole, chainSpec.startRole, {
        ...baseRelationship,
        kind: 'singleBone',
      });
      continue;
    }

    if (chainSpec.startRole === chainSpec.endRole) {
      continue;
    }

    addRelationship(relationshipsByRole, chainSpec.startRole, {
      ...baseRelationship,
      kind: 'start',
    });
    addRelationship(relationshipsByRole, chainSpec.endRole, {
      ...baseRelationship,
      kind: 'end',
    });

    for (const intermediateRole of rolePath.slice(1, -1)) {
      addRelationship(relationshipsByRole, intermediateRole, {
        ...baseRelationship,
        kind: 'intermediate',
      });
    }
  }

  return relationshipsByRole;
}

const ROLE_RELATIONSHIPS_BY_ROLE = buildRoleRelationshipIndex();

export function getChainRolePath(chainName: string): RoleChainPath | null {
  return CHAIN_ROLE_PATHS_BY_NAME.get(chainName) ?? null;
}

export function getRoleChainRelationships(
  roleName: string,
): readonly RoleChainRelationship[] {
  return ROLE_RELATIONSHIPS_BY_ROLE.get(roleName) ?? EMPTY_RELATIONSHIPS;
}

export function getSingleBoneChainNameForRole(roleName: string): string | null {
  const relationship = getRoleChainRelationships(roleName).find(
    (candidate) => candidate.kind === 'singleBone',
  );
  return relationship?.chainName ?? null;
}

export function buildChainDiagramPaths(): readonly RoleChainDiagramPath[] {
  return Object.values(CHAIN_DEFINITIONS).flatMap<RoleChainDiagramPath>(
    (chainSpec) => {
    const rolePath = CHAIN_ROLE_PATHS_BY_NAME.get(chainSpec.name)?.rolePath;
    if (!rolePath) return [];

    if (rolePath.length === 1) {
      const nodeDiagramKey =
        ROLE_NODE_DIAGRAM_KEY_BY_ROLE.get(chainSpec.startRole) ?? null;
      if (!nodeDiagramKey) return [];

        const singleNodePath: RoleChainDiagramPath = {
          chainName: chainSpec.name,
          startRole: chainSpec.startRole,
          endRole: chainSpec.endRole,
          rolePath,
          segments: [],
          nodeDiagramKey,
        };

        return [singleNodePath];
      }

      const segments = rolePath.slice(1).flatMap((toRole, index) => {
        const fromRole = rolePath[index];
        const diagramKey =
          DIAGRAM_KEY_BY_CONNECTION.get(buildConnectionKey(fromRole, toRole)) ?? null;
        if (!diagramKey) return [];

        return [{ from: fromRole, to: toRole, diagramKey }];
      });

      if (segments.length !== rolePath.length - 1) {
        return [];
      }

      const diagramPath: RoleChainDiagramPath = {
        chainName: chainSpec.name,
        startRole: chainSpec.startRole,
        endRole: chainSpec.endRole,
        rolePath,
        segments,
        nodeDiagramKey: null,
      };

      return [diagramPath];
    },
  );
}
